import { groundColour, surfaceHeight } from './chunk';
import { envTint, type EnvField } from './env';
import { scarTint, type Overlay } from './overlay';
import type { EarthStore } from './earth';
import type { Region } from './region';

/**
 * What the world beyond the streamed ring should look like.
 *
 * Three systems had the same hole and it was recorded three separate times as
 * "still open": a watershed-scale Strengthen, a burn scar and — since §20 — a
 * god-made landform were all **invisible past the streamed chunk ring**,
 * because `applyEnv`, `applyOverlay` and `applyEarth` are chunk transforms and
 * the horizon mesh is built once from the immutable Region.
 *
 * The fix is not a fourth special case. It is one sampler that says what the
 * ground is *now* at any position — baseline plus every sparse store — which
 * the far-field renderers can ask, and which reuses the very same `envTint`
 * and `scarTint` the chunks use rather than a second copy of the arithmetic
 * that would drift the first time either was tuned.
 *
 * Law 9 safe by construction: everything here reads. Nothing it touches can
 * write, and looking at the horizon changes nothing.
 */

export interface FarStores {
  earth?: EarthStore;
  env?: EnvField;
  overlay?: Overlay;
}

/** How far the ground has been moved from where the seed put it. */
export const farLift = (s: FarStores, x: number, z: number): number =>
  s.earth ? s.earth.at(x, z) : 0;

/**
 * The ground's height out here — the coarse baseline the horizon has always
 * drawn, plus whatever a god has moved. Detail 0, because the horizon mesh
 * has a vertex every ~156 m and the fine relief would only alias.
 */
export const farHeight = (
  region: Region, s: FarStores, x: number, z: number,
): number => surfaceHeight(region, x, z, 0) + farLift(s, x, z);

/**
 * The ground's colour out here: the baseline, then the land's remembered
 * condition, then any burn over the top — the same order the chunk transform
 * chain applies them in, so the far ground fades into the near rather than
 * off it.
 */
export function farColour(
  region: Region, s: FarStores,
  x: number, z: number, slope: number, now: number,
): [number, number, number] {
  // groundColour speaks in 0..255; every history tint is written in 0..1, and
  // the chunk builder normalises before the transforms run. Doing it here too
  // is what makes `envTint` and `scarTint` literally the same call in both
  // paths — the first version tinted a 0..255 base with 0..1 constants and
  // came out 160x wrong, which test/farfield.ts caught at the seam.
  const c0 = groundColour(region, x, z, slope);
  let c: [number, number, number] = [c0[0] / 255, c0[1] / 255, c0[2] / 255];

  const e = s.env?.at(x, z, now);
  if (e) c = envTint(c, e);

  const scar = s.overlay?.scarAt(x, z, now);
  if (scar) c = scarTint(c, scar);

  return c;
}

/**
 * A version stamp over every store that can change the far view. The horizon
 * rebuilds when this moves and not otherwise — the stores already keep these
 * as cache tags, so this costs three property reads a frame rather than a
 * scan of anything.
 */
export const farVersion = (s: FarStores): number =>
  (s.earth?.version ?? 0) * 1e9 + (s.env?.version ?? 0) * 1e4 + (s.overlay?.version ?? 0);

/** World-space box, for refreshing only what changed. */
export interface FarBounds { x0: number; z0: number; x1: number; z1: number }

/** The box enclosing every chunk key a store reports as dirty. */
export function boundsOfChunks(keys: readonly string[], chunkSize: number): FarBounds | null {
  if (!keys.length) return null;
  let x0 = Infinity, z0 = Infinity, x1 = -Infinity, z1 = -Infinity;
  for (const k of keys) {
    const [cx, cz] = k.split(',').map(Number);
    if (!Number.isFinite(cx) || !Number.isFinite(cz)) continue;
    x0 = Math.min(x0, cx * chunkSize);
    z0 = Math.min(z0, cz * chunkSize);
    x1 = Math.max(x1, (cx + 1) * chunkSize);
    z1 = Math.max(z1, (cz + 1) * chunkSize);
  }
  return Number.isFinite(x0) ? { x0, z0, x1, z1 } : null;
}

/* ── the far canopy's field ────────────────────────────────────────────────
 *
 * The far canopy already read ONE kind of history — `SereStore.ceilingField`,
 * a region-scale byte texture the shader samples per instance to drop any tree
 * taller than what could have regrown since the burn. That is the right
 * mechanism and it only needed widening: one texture, three channels, one
 * sample, and the other two stores ride along.
 *
 *     R  the sere's recovery ceiling, in metres (255 = climax, no limit)
 *     G  the land's remembered biomass, 128 = untouched, ±127 = ±1
 *     A  unused, opaque
 *     B  how far a god has moved the ground, 128 = where the seed put it
 *
 * What each channel is allowed to do is constrained by law 8 — a coarse LOD
 * may only OMIT:
 *
 *  · **R and G omit.** A burn drops the trees it took; hard withering drops
 *    them too, which is `applyEnv`'s own rule (it clears understory under
 *    −0.45) carried past the streamed ring.
 *  · **A blessing does nothing here.** It may not add far trees or grow them
 *    — that would be a coarse LOD inventing detail, and the near view would
 *    disagree the moment it streamed in.
 *  · **B is not a resize.** Moving a tree with the ground it stands on is what
 *    `applyEarth` already does per chunk; the far canopy has to match or a
 *    raised hill leaves its distant trees buried in it.
 */

/** Metres per byte in the lift channel — ±63.5 m, well past what a god affords. */
export const LIFT_BYTE_SCALE = 0.5;

/**
 * Where "no deviation" sits in the biomass and lift channels, and the span
 * either side of it. Symmetric on purpose: with 127.5 as the centre the byte
 * for untouched ground decoded to +0.004 rather than 0, which is harmless and
 * exactly the kind of thing that is not harmless the third time somebody
 * builds on it.
 */
export const FIELD_ZERO = 128;
export const FIELD_SPAN = 127;

export interface CanopyFieldSource extends FarStores {
  /** Fills `out` with the recovery ceiling in metres; 255 where unburned. */
  sere?: { ceilingField(now: number, n: number, cell: number, out: Uint8Array): void };
}

/**
 * Build the far canopy's field. `out` is `n * n * 4` RGBA bytes — alpha is
 * unused and stays opaque; three.js no longer offers a three-channel format.
 *
 * Deliberately whole-island rather than incremental: at n = 192 this is 36,864
 * texels, and the two extra channels are one store lookup each at a cell
 * centre. The sere's own pass already walked the island every time it changed.
 */
export function buildCanopyField(
  s: CanopyFieldSource,
  now: number, n: number, cellMetres: number,
  out: Uint8Array,
  scratch: Uint8Array,
): void {
  // R — the sere writes its own field in its own units
  scratch.fill(255);
  s.sere?.ceilingField(now, n, cellMetres, scratch);

  const half = (n * cellMetres) / 2;
  const haveEnv = !!s.env, haveEarth = !!s.earth;

  for (let j = 0; j < n; j++) for (let i = 0; i < n; i++) {
    const k = j * n + i;
    out[k * 4] = scratch[k];
    out[k * 4 + 3] = 255;

    if (!haveEnv && !haveEarth) {
      out[k * 4 + 1] = FIELD_ZERO;
      out[k * 4 + 2] = FIELD_ZERO;
      continue;
    }

    const x = -half + (i + 0.5) * cellMetres;
    const z = -half + (j + 0.5) * cellMetres;

    const b = haveEnv ? (s.env!.at(x, z, now)?.biomass ?? 0) : 0;
    out[k * 4 + 1] = Math.max(0, Math.min(255,
      Math.round(FIELD_ZERO + Math.max(-1, Math.min(1, b)) * FIELD_SPAN)));

    const lift = haveEarth ? s.earth!.at(x, z) : 0;
    out[k * 4 + 2] = Math.max(0, Math.min(255,
      Math.round(FIELD_ZERO + Math.max(-127, Math.min(127, lift / LIFT_BYTE_SCALE)))));
  }
}
